import type { ReactNode } from "react";
import { cn } from "../lib/cn.js";

/**
 * Phase C.2 共用 FormField — label + input slot + hint + error 标准化。
 * error 走 amber（非 bug 不红色，per CLAUDE.md 架构红线 §5 + dosia-design 铁律 2）。
 */
export function FormField({
  label,
  required,
  error,
  hint,
  children,
  className,
}: {
  label: ReactNode;
  required?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <label className={cn("flex flex-col gap-1 text-sm", className)}>
      <span className="text-dosia-text-secondary">
        {label}
        {required ? <span className="text-amber-700 ml-0.5">*</span> : null}
      </span>
      <span className="block">{children}</span>
      {hint && !error ? (
        <span className="text-xs text-dosia-text-dim">{hint}</span>
      ) : null}
      {error ? (
        <span className="text-xs text-amber-700" role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
}

/**
 * 标准 text input，复用现有 dosia-bg-input 风格（同 Login 页 / BasicInfo）。
 */
export function TextInput(
  props: React.InputHTMLAttributes<HTMLInputElement>,
): JSX.Element {
  const { className, ...rest } = props;
  return (
    <input
      {...rest}
      className={cn(
        "w-full rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm text-dosia-text",
        "focus:outline-none focus:border-dosia-accent",
        "placeholder:text-dosia-text-dim",
        className,
      )}
    />
  );
}

export function TextArea(
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement>,
): JSX.Element {
  const { className, ...rest } = props;
  return (
    <textarea
      {...rest}
      className={cn(
        "w-full rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm text-dosia-text font-mono",
        "focus:outline-none focus:border-dosia-accent",
        "placeholder:text-dosia-text-dim",
        className,
      )}
    />
  );
}

export function Select(
  props: React.SelectHTMLAttributes<HTMLSelectElement>,
): JSX.Element {
  const { className, ...rest } = props;
  return (
    <select
      {...rest}
      className={cn(
        "w-full rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm text-dosia-text",
        "focus:outline-none focus:border-dosia-accent",
        className,
      )}
    />
  );
}

/**
 * 提交/取消 button pair。primary 用 dosia-accent，secondary 走 border-only。
 */
export function PrimaryButton({
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        "rounded-md bg-dosia-accent text-dosia-bg px-3 py-1.5 text-sm font-medium",
        "hover:bg-dosia-accent-hover disabled:opacity-50 disabled:cursor-not-allowed",
        className,
      )}
    />
  );
}

export function SecondaryButton({
  className,
  danger,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }): JSX.Element {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        "rounded-md border px-3 py-1.5 text-sm",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        danger
          ? "text-dosia-danger border-dosia-danger/40 hover:bg-dosia-danger/10"
          : "border-dosia-border text-dosia-text-secondary hover:bg-dosia-bg-lighter",
        className,
      )}
    />
  );
}
